import React, { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import ExpenseDetail from "@/pages/ExpenseDetail";
import { MagnifyingGlass, FilePdf, Wallet } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtInr = (n) => "₹" + Math.round(n || 0).toLocaleString("en-IN");

export default function Payments() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [month, setMonth] = useState("");
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const isHubUser = user?.role === "hub_user";

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status: "paid" };
      if (q) params.q = q;
      if (month) params.month = month;
      const { data } = await api.get("/expenses", { params });
      setItems(data);
    } catch {
      toast.error("Could not load payments");
    } finally {
      setLoading(false);
    }
  }, [q, month]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const downloadInvoice = async (e, p) => {
    e.stopPropagation();
    try {
      const { data } = await api.get(`/expenses/${p.id}/invoice.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${p.invoice_no || "invoice-" + p.bill_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download invoice");
    }
  };

  const totalPaid = items.reduce((s, x) => s + (x.amount || 0), 0);

  return (
    <DashboardLayout>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Records</div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-900 flex items-center gap-2">
            <Wallet size={24} weight="duotone" className="text-green-600" /> Recent payments
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            All paid bills with Bank UTR numbers and downloadable invoices.
          </p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2 text-right" data-testid="payments-total">
          <div className="text-[10px] uppercase tracking-[0.16em] text-green-700">Total paid</div>
          <div className="font-heading text-xl font-bold font-mono text-green-800">{fmtInr(totalPaid)}</div>
        </div>
      </div>

      <div className="mt-5 bg-white border border-zinc-200 rounded-lg">
        <div className="flex flex-wrap items-center gap-2 p-4 border-b border-zinc-200">
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input data-testid="payments-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search hub, bill #, vendor, UTR…" className="pl-9" />
          </div>
          <Input type="month" data-testid="payments-month" value={month}
            onChange={(e) => setMonth(e.target.value)} className="w-[160px]" />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-zinc-500 border-b border-zinc-200">
                <th className="px-4 py-2.5 font-medium">Payment date</th>
                {!isHubUser && <th className="px-4 py-2.5 font-medium">Hub</th>}
                <th className="px-4 py-2.5 font-medium">Bill #</th>
                <th className="px-4 py-2.5 font-medium">Vendor</th>
                <th className="px-4 py-2.5 font-medium">Bank UTR number</th>
                <th className="px-4 py-2.5 font-medium">Invoice No</th>
                <th className="px-4 py-2.5 font-medium text-right">Amount</th>
                <th className="px-4 py-2.5 font-medium text-right">Invoice</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && !loading && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-zinc-500">No paid bills found.</td></tr>
              )}
              {items.map((p) => (
                <tr key={p.id} data-testid={`payment-row-${p.id}`}
                  onClick={() => setOpenId(p.id)}
                  className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50 cursor-pointer">
                  <td className="px-4 py-3 font-mono text-xs text-zinc-700">{p.payment_date || "—"}</td>
                  {!isHubUser && <td className="px-4 py-3 text-zinc-700">{p.hub || "—"}</td>}
                  <td className="px-4 py-3 font-mono text-xs text-zinc-700">{p.bill_number}</td>
                  <td className="px-4 py-3 text-zinc-700">{p.vendor_name}</td>
                  <td className="px-4 py-3 font-mono text-green-700" data-testid={`payment-utr-${p.id}`}>{p.utr || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-600">{p.invoice_no || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums text-zinc-900">{fmtInr(p.amount)}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={(e) => downloadInvoice(e, p)}
                      data-testid={`invoice-btn-${p.id}`}
                      title="Download payment invoice"
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-green-300 text-green-700 text-xs hover:bg-green-50 transition-colors duration-200"
                    >
                      <FilePdf size={14} /> Invoice
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {loading && <div className="p-4 text-xs text-zinc-500 font-mono">Loading…</div>}
      </div>

      <ExpenseDetail expenseId={openId} onClose={() => setOpenId(null)} onChanged={fetchList} role={user?.role} />
    </DashboardLayout>
  );
}
